// ===========================================================================
// Simulated YOLO Detector
// Produces detection outputs from simulation agents
// ===========================================================================

import {
  AgentPerson,
  AgentWeapon,
  Detection,
  SimulationParams,
  PERSON_RADIUS,
  WEAPON_SIZE,
  WORLD_WIDTH,
  WORLD_HEIGHT,
} from '../types';

/**
 * Generate detections from agents with simulated noise and errors
 */
export function runDetector(
  people: AgentPerson[],
  weapons: AgentWeapon[],
  params: SimulationParams
): Detection[] {
  const detections: Detection[] = [];

  // Process people
  for (const person of people) {
    // Simulate dropout (missed detection)
    if (Math.random() < params.dropoutRate) {
      continue;
    }

    // Add detection with noise
    const detection = createPersonDetection(person, params.detectionNoise);
    detections.push(detection);
  }

  // Process weapons
  for (const weapon of weapons) {
    // Weapons have lower dropout rate
    if (Math.random() < params.dropoutRate * 0.5) {
      continue;
    }

    const detection = createWeaponDetection(weapon, params.detectionNoise);
    detections.push(detection);
  }

  // Add false positives
  const numFP = poissonSample(params.falsePositiveRate * (people.length + 1));
  for (let i = 0; i < numFP; i++) {
    const fp = createFalsePositive(params.detectionNoise);
    detections.push(fp);
  }

  return detections;
}

/**
 * Create a detection for a person agent
 */
function createPersonDetection(
  person: AgentPerson,
  noiseLevel: number
): Detection {
  const bbox = computeBoundingBox(person.x, person.y, person.radius * 2, person.radius * 3);
  const noisyBbox = addBboxNoise(bbox, noiseLevel);

  // Confidence with noise
  const conf = clamp(person.confBase + gaussianNoise(noiseLevel), 0.5, 1.0);

  return {
    cls: 'person',
    conf,
    x1: noisyBbox.x1,
    y1: noisyBbox.y1,
    x2: noisyBbox.x2,
    y2: noisyBbox.y2,
    cx: (noisyBbox.x1 + noisyBbox.x2) / 2,
    cy: (noisyBbox.y1 + noisyBbox.y2) / 2,
    agentId: person.id,
  };
}

/**
 * Create a detection for a weapon
 */
function createWeaponDetection(
  weapon: AgentWeapon,
  noiseLevel: number
): Detection {
  const bbox = computeBoundingBox(weapon.x, weapon.y, WEAPON_SIZE * 2, WEAPON_SIZE * 2);
  const noisyBbox = addBboxNoise(bbox, noiseLevel * 0.5);

  const conf = clamp(0.9 + gaussianNoise(noiseLevel * 0.5), 0.6, 1.0);

  return {
    cls: 'weapon',
    conf,
    x1: noisyBbox.x1,
    y1: noisyBbox.y1,
    x2: noisyBbox.x2,
    y2: noisyBbox.y2,
    cx: (noisyBbox.x1 + noisyBbox.x2) / 2,
    cy: (noisyBbox.y1 + noisyBbox.y2) / 2,
    agentId: weapon.id,
  };
}

/**
 * Create a false positive detection
 */
function createFalsePositive(_noiseLevel: number): Detection {
  const x = PERSON_RADIUS + Math.random() * (WORLD_WIDTH - 2 * PERSON_RADIUS);
  const y = PERSON_RADIUS + Math.random() * (WORLD_HEIGHT - 2 * PERSON_RADIUS);
  const size = 20 + Math.random() * 30;

  const bbox = computeBoundingBox(x, y, size, size * 1.5);

  return {
    cls: Math.random() < 0.9 ? 'person' : 'weapon',
    conf: 0.5 + Math.random() * 0.3, // Lower confidence for FPs
    x1: bbox.x1,
    y1: bbox.y1,
    x2: bbox.x2,
    y2: bbox.y2,
    cx: x,
    cy: y,
    agentId: undefined,
  };
}

/**
 * Compute bounding box around center point
 */
function computeBoundingBox(
  cx: number,
  cy: number,
  width: number,
  height: number
): { x1: number; y1: number; x2: number; y2: number } {
  return {
    x1: cx - width / 2,
    y1: cy - height / 2,
    x2: cx + width / 2,
    y2: cy + height / 2,
  };
}

/**
 * Add noise to bounding box coordinates
 */
function addBboxNoise(
  bbox: { x1: number; y1: number; x2: number; y2: number },
  noiseLevel: number
): { x1: number; y1: number; x2: number; y2: number } {
  const noise = noiseLevel * 20; // Scale noise to pixels
  return {
    x1: bbox.x1 + gaussianNoise(noise),
    y1: bbox.y1 + gaussianNoise(noise),
    x2: bbox.x2 + gaussianNoise(noise),
    y2: bbox.y2 + gaussianNoise(noise),
  };
}

// ----------------------------- Utility Functions ---------------------------

function gaussianNoise(stddev: number): number {
  // Box-Muller transform
  const u1 = Math.random();
  const u2 = Math.random();
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  return z * stddev;
}

function poissonSample(lambda: number): number {
  if (lambda <= 0) return 0;
  const L = Math.exp(-lambda);
  let k = 0;
  let p = 1;
  do {
    k++;
    p *= Math.random();
  } while (p > L);
  return k - 1;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
